import { useSettings } from "../tools";
import { DialogActions } from "conference/components/dialogs";
import { Button, styled, Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AudioLevelIndicator from "./AudioLevelIndicator";
import SettingsWidget from "../media-settings/Widget";
import {
    isAudioOutputChangeSupported,
    useClientUISettings,
    useMediaDevices,
    DeviceSelector,
} from "@dvrt/web-phone-client";
import { conferenceLocalStorage } from "conference/tools";
import { defineMessages, FormattedMessage, MessageDescriptor } from "react-intl";
import { objectEntries } from "@dvrt/react-tools";

const errorMessages = defineMessages({
    "device.audioinput": {
        id: "errors.device.audioinput",
        defaultMessage: "Audio input device is unknown",
    },
    "device.videoinput": {
        id: "errors.device.videoinput",
        defaultMessage: "Video input device is unknown",
    },
    "device.audiooutput": {
        id: "errors.device.audiooutput",
        defaultMessage: "Audio output device is unknown",
    },
    displayName: {
        id: "errors.displayName",
        defaultMessage: "Display name is missing",
    },
});

const useDeviceErrors = (): MessageDescriptor[] => {
    const mediaDevices = useMediaDevices();
    const clientUISettings = useClientUISettings();
    const settings = useSettings();

    const res: MessageDescriptor[] = [];

    if (clientUISettings.selectedMediaDevices === null) return [];

    for (const [type, devices] of objectEntries(mediaDevices.devices)) {
        if (type === "audiooutput" && !isAudioOutputChangeSupported) continue;

        if (type === "videoinput" && !settings.video) continue;

        if (type === "audioinput" && !settings.audio) continue;

        if (type === "audiooutput" && devices.length == 0) continue;

        const selected = clientUISettings.selectedMediaDevices[type];
        if (devices.filter((d) => d.deviceId === selected).length === 0) res.push(errorMessages[`device.${type}`]);
    }

    return res;
};

const TooltipTitle = ({ errors }: { errors: MessageDescriptor[] }) => {
    if (errors.length === 0)
        return (
            <FormattedMessage
                id="meetingPage.prepare.nextButtonTooltip"
                defaultMessage="Click to join to the meeting"
            />
        );

    return (
        <div>
            {errors.map((err) => (
                <div key={err.id}>
                    <FormattedMessage {...err} />
                </div>
            ))}
        </div>
    );
};

const FooterDiv = styled("div")(({ theme }) => ({
    width: "100%",
    padding: "0.2em 0.5em",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: "1fr 1fr",
        gridTemplateAreas: `
            "controls controls"
            "backButton joinButton"
        `,
    },
}));

const ControlsDiv = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    margin: "0 1em",
    [theme.breakpoints.down("sm")]: {
        gridArea: "controls",
        justifySelf: "center",
    },
}));

const BackButton = styled(Button)(({ theme }) => ({
    [theme.breakpoints.down("sm")]: {
        gridArea: "backButton",
        placeSelf: "center self-start",
    },
}));

const JoinButtonContainer = styled("span")(({ theme }) => ({
    [theme.breakpoints.down("sm")]: {
        gridArea: "joinButton",
        placeSelf: "center self-end",
    },
}));

export default function Footer() {
    const settings = useSettings();
    const mediaDevices = useMediaDevices();

    const errors: MessageDescriptor[] = [];

    if (!settings.displayName) errors.push(errorMessages.displayName);

    errors.push(...useDeviceErrors());

    const nextIsDisabled = errors.length > 0;

    const onJoin = () => {
        settings.stage++;
        conferenceLocalStorage.selectedMediaDevices ??= mediaDevices.defaults;
    };

    return (
        <DialogActions>
            <FooterDiv>
                <BackButton variant="contained" color="primary" onClick={() => settings.stage--}>
                    <FontAwesomeIcon icon="chevron-left" style={{ marginRight: "0.5em" }} />
                    <FormattedMessage id="meetingPage.prepare.deviceSelector.back" defaultMessage="Back" />
                </BackButton>
                <ControlsDiv>
                    <AudioLevelIndicator />
                    <SettingsWidget />
                    <DeviceSelector />
                </ControlsDiv>
                <Tooltip title={<TooltipTitle errors={errors} />} arrow placement="top">
                    <JoinButtonContainer>
                        <Button variant="contained" color="primary" onClick={onJoin} disabled={nextIsDisabled}>
                            <FormattedMessage id="meetingPage.prepare.deviceSelector.join" defaultMessage="Join" />
                            <FontAwesomeIcon icon="sign-in-alt" style={{ marginLeft: "0.5em" }} />
                        </Button>
                    </JoinButtonContainer>
                </Tooltip>
            </FooterDiv>
        </DialogActions>
    );
}
